import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import { saveReservation } from '../../utils/reservationStorage';
import type { Reservation } from '../../mocks/reservations';

interface TravelerItem {
  adults: number;
  children: number;
  departureDate: string;
}

export default function RezervastonPage() {
  const { items, clearCart } = useCart();
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
  });

  const [travelers, setTravelers] = useState<Record<string, TravelerItem>>(() =>
    Object.fromEntries(
      items.map((item) => [item.tour.id, { adults: 1, children: 0, departureDate: '' }])
    )
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const updateTraveler = (
    tourId: string,
    field: keyof TravelerItem,
    value: number | string
  ) => {
    setTravelers((prev) => ({
      ...prev,
      [tourId]: { ...prev[tourId], [field]: value },
    }));
  };

  const getItemTotal = (tourId: string, price: number) => {
    const t = travelers[tourId];
    if (!t) return price;
    const total = t.adults + t.children;
    return price * (total < 1 ? 1 : total);
  };

  const tlItems = items.filter((i) => i.tour.currency === 'TL');
  const eurItems = items.filter((i) => i.tour.currency === '€');

  const tlTotal = useMemo(
    () => tlItems.reduce((s, i) => s + getItemTotal(i.tour.id, i.tour.price), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [travelers, tlItems]
  );

  const eurTotal = useMemo(
    () => eurItems.reduce((s, i) => s + getItemTotal(i.tour.id, i.tour.price), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [travelers, eurItems]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
    if (name === 'specialRequests') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    setErrorMsg('');

    try {
      for (const item of items) {
        const t = travelers[item.tour.id] || { adults: 1, children: 0, departureDate: '' };
        const reservation: Reservation = {
          id: `r_${Date.now()}_${item.tour.id}`,
          tourId: item.tour.id,
          tourName: item.tour.name,
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          email: customerInfo.email,
          phone: customerInfo.phone,
          departureDate: t.departureDate,
          adults: t.adults,
          children: t.children,
          specialRequests: customerInfo.specialRequests,
          status: 'beklemede',
          createdAt: new Date().toISOString(),
          totalPrice: getItemTotal(item.tour.id, item.tour.price),
          currency: item.tour.currency,
        };
        await saveReservation(reservation);
      }

      // Readdy form (bağımsız)
      const params = new URLSearchParams({
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        specialRequests: customerInfo.specialRequests,
        tourCount: String(items.length),
      });
      fetch('https://readdy.ai/api/form/d70le5v9hgr6oot2r120', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      }).catch(() => {});

      clearCart();
      setSubmitted(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setErrorMsg(`Rezervasyon gönderilemedi: ${msg}. Lütfen tekrar deneyin.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && !submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 flex items-center justify-center bg-orange-50 rounded-full mx-auto mb-6">
              <i className="ri-shopping-cart-2-line text-orange-400 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Sepetiniz Boş</h2>
            <p className="text-gray-500 mb-8">Rezervasyon yapabilmek için önce sepetinize tur ekleyin.</p>
            <Link
              to="/sepet"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Sepete Dön
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 pt-20">
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-check-double-line text-green-500 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Rezervasyon Talebiniz Alındı!
            </h2>
            <p className="text-gray-500 mb-2">
              Tur danışmanımız en kısa sürede{' '}
              <span className="font-semibold text-gray-700">{customerInfo.email}</span>{' '}
              adresine dönüş yapacaktır.
            </p>
            <p className="text-sm text-gray-400 mb-8">
              Rezervasyonunuz onaylandığında e-posta ile bildirim alacaksınız.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line" />
                Ana Sayfaya Dön
              </Link>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-8 py-3 rounded-full font-semibold cursor-pointer whitespace-nowrap"
              >
                Daha Fazla Tur Ara
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      <div className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/sepet" className="hover:text-orange-500 transition-colors cursor-pointer">
              Sepet
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-700 font-medium">Rezervasyon Formu</span>
          </div>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rezervasyon Formu</h1>
              <p className="text-gray-500 mt-1 text-sm">
                Bilgilerinizi doldurun, kişi sayısını ayarlayın, toplam tutar otomatik hesaplanır
              </p>
            </div>
            <Link
              to="/sepet"
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Sepete Dön
            </Link>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Customer Info */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center gap-2.5 mb-5">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
                      <i className="ri-user-line text-orange-500 text-sm" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Müşteri Bilgileri</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Ad *</label>
                      <input
                        name="firstName"
                        type="text"
                        required
                        value={customerInfo.firstName}
                        onChange={handleChange}
                        placeholder="Adınız"
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Soyad *</label>
                      <input
                        name="lastName"
                        type="text"
                        required
                        value={customerInfo.lastName}
                        onChange={handleChange}
                        placeholder="Soyadınız"
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">E-posta *</label>
                      <input
                        name="email"
                        type="email"
                        required
                        value={customerInfo.email}
                        onChange={handleChange}
                        placeholder="ornek@mail.com"
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Telefon *</label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        value={customerInfo.phone}
                        onChange={handleChange}
                        placeholder="0532 XXX XX XX"
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Özel İstekler
                        <span className={`ml-1 ${charCount > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                          ({charCount}/500)
                        </span>
                      </label>
                      <textarea
                        name="specialRequests"
                        rows={3}
                        maxLength={500}
                        value={customerInfo.specialRequests}
                        onChange={handleChange}
                        placeholder="Özel istekleriniz, sorularınız..."
                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Per-tour passenger selection */}
                {items.map((item) => {
                  const t = travelers[item.tour.id] || { adults: 1, children: 0, departureDate: '' };
                  const subtotal = getItemTotal(item.tour.id, item.tour.price);
                  const totalPeople = t.adults + t.children;

                  return (
                    <div key={item.tour.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                      {/* Tour header */}
                      <div className="flex gap-4 p-5 border-b border-gray-50">
                        <div className="w-20 h-16 flex-shrink-0 rounded-xl overflow-hidden">
                          <img
                            src={item.tour.image}
                            alt={item.tour.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-orange-500 font-medium mb-0.5">
                            {item.tour.duration} {item.tour.durationUnit}
                          </p>
                          <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">
                            {item.tour.name}
                          </h3>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.tour.currency === 'TL'
                              ? `₺${item.tour.price.toLocaleString('tr-TR')} kişi başı`
                              : `€${item.tour.price.toLocaleString('tr-TR')} kişi başı`}
                          </p>
                        </div>
                      </div>

                      {/* Passenger & Date selectors */}
                      <div className="p-5 space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">
                            Tercih Ettiğiniz Tarih
                          </label>
                          <input
                            type="date"
                            value={t.departureDate}
                            onChange={(e) =>
                              updateTraveler(item.tour.id, 'departureDate', e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Adults */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Yetişkin
                            </label>
                            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                              <button
                                type="button"
                                onClick={() =>
                                  updateTraveler(
                                    item.tour.id,
                                    'adults',
                                    Math.max(1, t.adults - 1)
                                  )
                                }
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-subtract-line text-sm" />
                              </button>
                              <span className="flex-1 text-center text-sm font-semibold text-gray-800">
                                {t.adults}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  updateTraveler(item.tour.id, 'adults', t.adults + 1)
                                }
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-add-line text-sm" />
                              </button>
                            </div>
                          </div>

                          {/* Children */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Çocuk (0-12 yaş)
                            </label>
                            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                              <button
                                type="button"
                                onClick={() =>
                                  updateTraveler(
                                    item.tour.id,
                                    'children',
                                    Math.max(0, t.children - 1)
                                  )
                                }
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-subtract-line text-sm" />
                              </button>
                              <span className="flex-1 text-center text-sm font-semibold text-gray-800">
                                {t.children}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  updateTraveler(item.tour.id, 'children', t.children + 1)
                                }
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-add-line text-sm" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Item total */}
                        <div className="flex items-center justify-between bg-orange-50 rounded-xl px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-group-line text-orange-500 text-sm" />
                            </div>
                            <span>{totalPeople} kişi × {item.tour.currency === 'TL' ? `₺${item.tour.price.toLocaleString('tr-TR')}` : `€${item.tour.price.toLocaleString('tr-TR')}`}</span>
                          </div>
                          <span className="text-base font-bold text-orange-500">
                            {item.tour.currency === 'TL'
                              ? `₺${subtotal.toLocaleString('tr-TR')}`
                              : `€${subtotal.toLocaleString('tr-TR')}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right: Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-28">
                  <h3 className="font-bold text-gray-900 text-base mb-5">Sipariş Özeti</h3>

                  <div className="space-y-3 mb-5">
                    {items.map((item) => {
                      const subtotal = getItemTotal(item.tour.id, item.tour.price);
                      const t = travelers[item.tour.id] || { adults: 1, children: 0, departureDate: '' };
                      return (
                        <div key={item.tour.id} className="flex justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-700 font-medium leading-snug line-clamp-2">
                              {item.tour.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t.adults + t.children} kişi
                            </p>
                          </div>
                          <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                            {item.tour.currency === 'TL'
                              ? `₺${subtotal.toLocaleString('tr-TR')}`
                              : `€${subtotal.toLocaleString('tr-TR')}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-gray-100 pt-4 mb-5 space-y-2">
                    {tlTotal > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-900">Toplam (TL)</span>
                        <span className="text-xl font-bold text-orange-500">
                          ₺{tlTotal.toLocaleString('tr-TR')}
                        </span>
                      </div>
                    )}
                    {eurTotal > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-900">Toplam (€)</span>
                        <span className="text-xl font-bold text-orange-500">
                          €{eurTotal.toLocaleString('tr-TR')}
                        </span>
                      </div>
                    )}
                  </div>

                  {errorMsg && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 mb-4">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500 text-sm" />
                      </div>
                      <p className="text-xs text-red-600">{errorMsg}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3.5 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        <span>Gönderiliyor...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        <span>Rezervasyon Talebi Gönder</span>
                      </>
                    )}
                  </button>

                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-400 justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shield-check-line text-green-500" />
                    </div>
                    <span>Güvenli rezervasyon sistemi</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
